import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { alpha } from '@mui/material/styles';
import LetterTile from './LetterTile';
import { LETTER_POINTS } from './config/gameConfig';

function RackSlot({
  index,
  tile,
  discardMode,
  selectedDiscardTileIds,
  onToggleDiscardTile,
  onTileEnter,
  onTileLeave,
  theme,
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `rack-slot:${index}`,
    data: {
      type: 'rack-slot',
      index,
    },
  });

  if (!tile) {
    return (
      <Box
        ref={setNodeRef}
        sx={{
          aspectRatio: '1 / 1',
          borderRadius: 0.75,
          border: `1px dashed ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
          backgroundColor: isOver
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.background.paper, 0.28),
          display: 'grid',
          placeItems: 'center',
          transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
          transform: isOver ? 'translateY(-1px)' : 'translateY(0)',
        }}
      >
        <Typography sx={{ color: theme.palette.scrabble.tileText, opacity: 0.4 }}>•</Typography>
      </Box>
    );
  }

  const isDiscardSelected = discardMode && selectedDiscardTileIds.includes(tile.id);

  return (
    <Box
      ref={setNodeRef}
      onMouseEnter={() => onTileEnter(tile)}
      onMouseLeave={onTileLeave}
      onPointerDown={() => { if (!discardMode) onTileEnter(tile); }}
      onClick={() => {
        if (discardMode) {
          onToggleDiscardTile?.(tile.id);
        }
      }}
      sx={{
        borderRadius: 0.75,
        border: isDiscardSelected
          ? (themeValue) => `4px solid ${themeValue.palette.warning.main}`
          : isOver
            ? (themeValue) => `2px solid ${themeValue.palette.primary.main}`
            : '2px solid transparent',
        boxShadow: isDiscardSelected
          ? (themeValue) => `0 0 0 2px ${alpha(themeValue.palette.warning.main, 0.28)}`
          : isOver
            ? (themeValue) => `0 0 0 2px ${alpha(themeValue.palette.primary.main, 0.16)}`
            : 'none',
        cursor: discardMode ? 'pointer' : 'grab',
        touchAction: discardMode ? 'auto' : 'none',
        transition: 'box-shadow 120ms ease, border-color 120ms ease, transform 120ms ease',
        transform: isOver ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <LetterTile
        tile={{ ...tile, points: LETTER_POINTS[tile.letter] || 0 }}
        dragData={{
          source: 'rack',
          tile,
          tileId: tile.id,
          fromIndex: index,
        }}
        draggable={!discardMode}
      />
    </Box>
  );
}

function TileRack({
  tiles = [],
  onInspectTile,
  discardMode = false,
  selectedDiscardTileIds = [],
  onToggleDiscardTile,
}) {
  const theme = useTheme();
  const handleTileEnter = React.useCallback(
    (tile) => {
      if (onInspectTile) {
        onInspectTile(tile);
      }
    },
    [onInspectTile]
  );

  const handleTileLeave = React.useCallback(() => {
    if (onInspectTile) {
      onInspectTile(null);
    }
  }, [onInspectTile]);

  const { setNodeRef, isOver } = useDroppable({
    id: 'rack-drop',
    data: {
      type: 'rack-drop',
    },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        width: '100%',
        padding: { xs: 0.58, sm: 1 },
        borderRadius: 3,
        backgroundColor: theme.palette.scrabble.rack,
        border: `1px solid ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
        display: 'grid',
        gridTemplateColumns: `repeat(7, minmax(44px, 1fr))`,
        gap: { xs: 0.46, sm: 0.75 },
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        boxShadow: isOver
          ? 4
          : `inset 0 1px 0 ${alpha('#fff', 0.22)}, inset 0 -1px 0 ${alpha(
              theme.palette.scrabble.tileText,
              0.12
            )}`,
      }}
    >
      {Array.from({ length: 7 }).map((_, index) => {
        const tile = tiles[index] || null;

        return (
          <RackSlot
            key={tile?.id || `empty-${index}`}
            index={index}
            tile={tile}
            discardMode={discardMode}
            selectedDiscardTileIds={selectedDiscardTileIds}
            onToggleDiscardTile={onToggleDiscardTile}
            onTileEnter={handleTileEnter}
            onTileLeave={handleTileLeave}
            theme={theme}
          />
        );
      })}
    </Box>
  );
}

export default TileRack;